import { useState, useRef, useMemo, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ResponsiveFormSection } from "@/components/form/responsive-form";
import {
  getOrganisationUserLabelsFn,
  organisationUserLabelsQueryOptions,
  updateOrganisationUserLabelsFn,
} from "@/server/organisations";
import { UserLabel, UserLabelList } from "@/lib/mma/types.gen";
import { userLabelsQueryOptions } from "@/server/user-labels";
import _ from "lodash";
import { UserLabelsTable } from "@/routes/_dashboard/user-labels/-components/user-labels-table";
import { ConfirmDialog } from "@/components/confirm-dialog";

export const Route = createFileRoute("/_dashboard/organisations/$orgId/user-labels")({
  component: RouteComponent,
  loader: async () => {
    return { crumb: "User Labels" };
  },
});

function RouteComponent() {
  const params = Route.useParams();
  const queryClient = useQueryClient();
  const organisationUserLabelsQuery = useQuery(organisationUserLabelsQueryOptions(Number(params.orgId)));
  const userLabelsData = organisationUserLabelsQuery.data;
  const allUserLabelsQuery = useQuery(userLabelsQueryOptions());
  const allUserLabels = allUserLabelsQuery.data || [];

  useEffect(() => {
    if (!params.orgId) return;
    void organisationUserLabelsQuery.refetch();
    void allUserLabelsQuery.refetch();
  }, [params.orgId, organisationUserLabelsQuery.refetch, allUserLabelsQuery.refetch]);

  const mappedUserLabels = useMemo(
    () =>
      allUserLabels.filter((userLabel: UserLabel) =>
        userLabelsData?.some((userLabelDataKey: string) => userLabelDataKey === userLabel.key)
      ),
    [allUserLabels, userLabelsData]
  );

  const [userLabels, setUserLabels] = useState<UserLabel[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  //This is needed to enable the user to cancel the changes and revert to the initial user labels.
  const initialUserLabelsRef = useRef<UserLabel[]>([]);

  useEffect(() => {
    if (mappedUserLabels && !_.isEqual(userLabels, mappedUserLabels)) {
      setUserLabels(mappedUserLabels);
      initialUserLabelsRef.current = mappedUserLabels;
    }
  }, [mappedUserLabels]);

  const updateOrganisationUserLabelsMutation = useMutation({
    mutationFn: (userLabels: UserLabelList) =>
      updateOrganisationUserLabelsFn({ data: { orgId: Number(params.orgId), payload: userLabels } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisationUserLabels", Number(params.orgId)] });
    },
    onError: (error) => {
      console.error("Error updating user labels:", error);
    },
  });

  const updateOrganisationUserLabels = async (userLabels: UserLabelList) => {
    const updateOrganisationUserLabelsPromise = updateOrganisationUserLabelsMutation.mutateAsync(userLabels);
    toast.promise(updateOrganisationUserLabelsPromise, {
      loading: "Updating user labels...",
      success: "User labels updated",
      error: (error) => error.message || "Failed to update user labels",
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const userLabelsKeyArray: string[] = userLabels
        .map((userLabel: UserLabel) => userLabel.key || "")
        .filter((key: string) => key !== "");
      await updateOrganisationUserLabels(userLabelsKeyArray);
      initialUserLabelsRef.current = userLabels;
      await queryClient.invalidateQueries({ queryKey: ["organisationUserLabels", Number(params.orgId)] });
      setIsDirty(false);
    } catch (error) {
      console.error("Error updating user labels:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setUserLabels(initialUserLabelsRef.current);
    setIsDirty(false);
    setCancelDialogOpen(false);
  };

  const canSubmit = isDirty && !isSaving;

  const availableUserLabels = useMemo(
    () => allUserLabels.filter((a: any) => !userLabels.some((qa: any) => qa.key === a.key)),
    [userLabels, allUserLabels]
  );

  return (
    <ResponsiveFormSection>
      <form onSubmit={handleSave}>
        <UserLabelsTable
          labels={userLabels}
          organisationId={Number(params.orgId)}
          availableUserLabels={availableUserLabels}
          setUserLabels={setUserLabels}
          setIsDirty={setIsDirty}
        />
        <div className="flex gap-2 mt-4">
          <Button type="submit" disabled={!canSubmit || _.isEqual(userLabels, initialUserLabelsRef.current)}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCancelDialogOpen(true);
            }}
          >
            Cancel
          </Button>
        </div>
      </form>
      <ConfirmDialog
        open={cancelDialogOpen}
        title="Discard changes"
        description="Are you sure you want to undo all unsaved changes? This will reset the form back to its initial state."
        confirmLabel="Discard"
        cancelLabel="Cancel"
        confirmVariant="destructive"
        onConfirm={handleCancel}
        onCancel={() => {
          setCancelDialogOpen(false);
        }}
      />
    </ResponsiveFormSection>
  );
}
